import React, { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import { useBranches } from '../../hooks/useBranches';
import { resolveBranchRef } from '../../utils/branchResolver';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import { useAcademySettings } from '../../hooks/useAcademySettings';

const LINK_TYPES = new Set([
  'payhere_link',
  'stripe_link',
  'paypal_link',
  'wise_link',
  'other_link',
]);

const describeMethod = (method, academy) => {
  if (!method) return '';
  if (method.type === 'bank_account') {
    const { bankName, accountName, accountNumber, branch, swiftCode } =
      method.details || {};
    return [
      bankName ? `Bank: ${bankName}` : null,
      accountName ? `Account name: ${accountName}` : null,
      accountNumber ? `Account number: ${accountNumber}` : null,
      branch ? `Branch: ${branch}` : null,
      swiftCode ? `SWIFT: ${swiftCode}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (method.type === 'cash') return `Pay in cash at ${academy?.name || 'the academy'}.`;
  if (method.type === 'card') return 'Pay by card at reception.';
  if (method.type === 'cheque') return `Make cheque payable to ${academy?.name || ''}.`;
  if (LINK_TYPES.has(method.type)) {
    return 'Complete payment via the secure checkout link.';
  }
  return '';
};

const JoinRequestModal = ({ isOpen, onClose, onSubmit, batch }) => {
  const { byId: branchesById, bySlug: branchesBySlug } = useBranches();
  const { methods } = usePaymentMethods('student');
  const { academy } = useAcademySettings();
  const [paymentPlan, setPaymentPlan] = useState('pay_later');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (!paymentMethodId && methods.length) {
      setPaymentMethodId(methods[0].id);
    }
  }, [isOpen, methods, paymentMethodId]);

  const activeMethod = useMemo(
    () => methods.find((method) => method.id === paymentMethodId) || null,
    [methods, paymentMethodId],
  );

  const branchName =
    resolveBranchRef(batch, { byId: branchesById, bySlug: branchesBySlug })?.name ||
    'Branch not set';

  const handleSubmit = () => {
    if (!methods.length) {
      setError('No payment methods configured. Contact the academy.');
      return;
    }
    if (paymentPlan === 'pay_now' && !receiptNumber.trim() && !file) {
      setError('Add a receipt file or reference number before sending payment.');
      return;
    }

    setError('');
    onSubmit({
      paymentPlan,
      paymentMethod: activeMethod?.type || 'bank_transfer',
      paymentMethodId: activeMethod?.id || null,
      paymentMethodLabel: activeMethod?.label || '',
      receiptNumber,
      note,
      file,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-stone-900/45 p-4 sm:items-center">
      <div className="w-full max-w-2xl rounded-[28px] border border-primary-100 bg-white p-5 shadow-xl sm:p-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-stone-900">Register for batch</h2>
          <p className="text-sm leading-6 text-stone-600">
            Review the payment method and instructions first. Then send the
            payment now or finish it later from your Payments page.
          </p>
          {batch ? (
            <p className="text-sm font-medium text-stone-700">
              {batch.batchId || 'Batch'} · {branchName}
            </p>
          ) : null}
          {academy?.name ? (
            <p className="text-xs text-stone-500">
              Payee: <span className="font-medium text-stone-700">{academy.name}</span>
            </p>
          ) : null}
        </div>

        {error ? (
          <div className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="rounded-2xl bg-stone-50 p-4">
              <p className="text-sm font-semibold text-stone-900">Payment methods</p>
              {methods.length === 0 ? (
                <p className="mt-3 text-sm text-stone-600">
                  No payment methods are configured yet. Please contact the
                  academy.
                </p>
              ) : (
                <div className="mt-3 space-y-3">
                  {methods.map((method) => {
                    const description = describeMethod(method, academy);
                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setPaymentMethodId(method.id)}
                        className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                          paymentMethodId === method.id
                            ? 'border-primary-300 bg-white'
                            : 'border-stone-200 bg-white hover:border-stone-300'
                        }`}
                      >
                        <p className="text-sm font-semibold text-stone-900">
                          {method.label}
                        </p>
                        {description ? (
                          <p className="mt-1 text-sm text-stone-600">{description}</p>
                        ) : null}
                        {method.instructions ? (
                          <p className="mt-1 text-xs text-stone-500">
                            {method.instructions}
                          </p>
                        ) : null}
                        {LINK_TYPES.has(method.type) && method.details?.checkoutUrl ? (
                          <a
                            href={method.details.checkoutUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
                          >
                            Open payment page
                          </a>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-stone-50 p-4">
              <p className="text-sm font-semibold text-stone-900">Choose how to continue</p>
              <div className="mt-3 grid gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentPlan('pay_now')}
                  className={`rounded-2xl border px-4 py-3 text-left transition ${
                    paymentPlan === 'pay_now'
                      ? 'border-primary-300 bg-white'
                      : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                >
                  <p className="text-sm font-semibold text-stone-900">Pay now</p>
                  <p className="mt-1 text-sm text-stone-600">
                    Send your request together with the receipt or reference number.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentPlan('pay_later')}
                  className={`rounded-2xl border px-4 py-3 text-left transition ${
                    paymentPlan === 'pay_later'
                      ? 'border-primary-300 bg-white'
                      : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                >
                  <p className="text-sm font-semibold text-stone-900">Pay later</p>
                  <p className="mt-1 text-sm text-stone-600">
                    Send the registration request first. The payment reminder will
                    stay on your dashboard and Payments page.
                  </p>
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {paymentPlan === 'pay_now' ? (
              <>
                <div className="rounded-2xl bg-primary-50 p-4 text-sm leading-6 text-primary-900">
                  After payment, add the receipt image, PDF, or bank reference
                  number here.
                </div>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700">
                    Receipt or reference number
                  </span>
                  <input
                    type="text"
                    value={receiptNumber}
                    onChange={(e) => setReceiptNumber(e.target.value)}
                    className="block min-h-[48px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="Enter number"
                  />
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700">
                    Receipt image or PDF
                  </span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="block w-full text-sm text-stone-600 file:mr-4 file:rounded-full file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:font-semibold file:text-primary-700"
                  />
                </label>
              </>
            ) : (
              <div className="rounded-2xl bg-stone-50 p-4 text-sm leading-6 text-stone-600">
                You can send the request now and pay later. After you make the
                payment, open the Payments page and submit the receipt or
                reference number there.
              </div>
            )}

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-stone-700">
                Note
              </span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="block w-full rounded-2xl border border-stone-300 px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                placeholder="Optional note"
                rows="4"
              />
            </label>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {paymentPlan === 'pay_now' ? 'Send request and payment' : 'Send request'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JoinRequestModal;
